import React from 'react';
import { OrderContext, OrderContextProps } from '../context/OrderContext';
import { Order } from '../types/order';
import { useAppDispatch, useAppSelector } from '../ui/pages/store';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { v4 } from 'uuid';
import { GetOrdersResponse, GetOrdersVariables, OrderQueries } from '../graphql/order.graphql';
import useLazyQuery from '../hooks/useLazyQuery';

const orderState = {
  items: new Map<string, Order>(),
  loading: false,
};

interface OrderState {
  key: string;
}

export const orderSlice = createSlice({
  name: 'order',
  initialState: { key: v4() } as OrderState,
  reducers: {
    reset: state => {
      orderState.items = new Map();
      orderState.loading = true;
      state.key = v4();
    },
    set: (state, action: PayloadAction<Order[]>) => {
      orderState.items = new Map();
      action.payload.forEach(o => orderState.items.set(o.id, new Order(o)));
      state.key = v4();
    },
    add: (state, action: PayloadAction<Order[]>) => {
      action.payload.forEach(o => orderState.items.set(o.id, new Order(o)));
      orderState.items = new Map(orderState.items);
      state.key = v4();
    },
    delete: (state, action: PayloadAction<Order[]>) => {
      action.payload.forEach(o => orderState.items.delete(o.id));
      orderState.items = new Map(orderState.items);
      state.key = v4();
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      orderState.loading = action.payload;
      state.key = v4();
    },
  },
});

interface OrderProviderProps {
  children: React.ReactNode;
}

export default function OrderProvider({ children }: OrderProviderProps) {
  const dispatch = useAppDispatch();
  const key = useAppSelector(state => state.orders.key);

  const [loadAllOrders, { loading: loadAllOrdersLoading }] = useLazyQuery<GetOrdersResponse, GetOrdersVariables>(
    OrderQueries.get,
    {
      variables: { since: new Date(0) },
      onCompleted: async response => {
        dispatch(orderSlice.actions.add(response.orders.data));
        if (response.orders.hasNext === true) {
          await loadAllOrders({
            variables: {
              page: ++response.orders.page,
              since: new Date(0),
            },
          });
        }
      },
    },
  );

  const orderContext: OrderContextProps = {
    orders: orderState.items,
    ordersLoading: orderState.loading || loadAllOrdersLoading,
    setOrders: (orders: Map<string, Order>) => dispatch(orderSlice.actions.set([...orders.values()])),
    loadAllOrders,
  };

  return <OrderContext.Provider value={orderContext}>{children}</OrderContext.Provider>;
}
